import React, { useEffect, useRef, useState } from 'react';

import getMediaInfo from 'src/utils/getMediaInfo';
import stopPropagation from 'src/utils/stopPropagation';

import type { MediumProps } from 'src/components/other/MediumComponent';
import type { Medium } from 'src/types/__generated__/graphql';
import AudioPlayer from 'src/components/ui/AudioPlayer';
import useAudio from 'src/hooks/useAudio';
import { styledTag } from 'src/utils/styled';

interface CustomAudioMediumProps extends MediumProps {
    controls?: boolean;
    thumbnail?: Medium;
}

const HiddenAudio = styledTag('audio')`
    display: none;
`;

const CustomAudio: React.FC<CustomAudioMediumProps> = ({ media, controls, thumbnail = null }) => {
    const timeSliderSteps = 1000;
    const volumeSliderSteps = 10;
    const mediaInfo = getMediaInfo(media.url);
    const audioRef = useRef<HTMLAudioElement>(null);
    const [timeSliderValue, setTimeSliderValue] = useState<number>(0);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [volume, setVolume] = useState<number>(10);
    const [muted, setMuted] = useState<boolean>(false);

    const { pause } = useAudio();

    useEffect(() => {
        const stopHandler = (ev: CustomEvent<HTMLAudioElement>) => {
            if (ev.detail !== audioRef.current) {
                audioRef.current?.pause();
            }
        };

        window.addEventListener('stopmedia', stopHandler as EventListener);
        return () => {
            window.removeEventListener('stopmedia', stopHandler as EventListener);
        };
    }, []);

    useEffect(() => {
        setTimeSliderValue((currentTime / media.duration) * timeSliderSteps);
    }, [currentTime, media]);

    useEffect(() => {
        if (audioRef.current?.volume) audioRef.current.volume = volume / volumeSliderSteps;
    }, [volume]);

    useEffect(() => {
        if (audioRef.current) audioRef.current.muted = muted;
    }, [muted]);

    const playPauseHandle = async () => {
        // Pause the global audio
        pause();

        if (audioRef.current?.paused) {
            await audioRef.current.play();
        } else {
            audioRef.current?.pause();
        }
    };

    const updateCurrentTime = (value: number): void => {
        if (audioRef.current?.currentTime) audioRef.current.currentTime = (value * media.duration) / timeSliderSteps;
    };

    const volumeSliderHandle = (_volume: number): void => {
        setVolume(_volume);
        setMuted(false);
    };

    const onTimeUpdateHandle = (e: React.SyntheticEvent<HTMLMediaElement | HTMLAudioElement>) => {
        const { currentTime: _currentTime } = e.target as HTMLMediaElement;
        setCurrentTime(_currentTime);
    };

    return (
        <>
            <HiddenAudio
                ref={audioRef}
                key={media.url}
                onClick={stopPropagation}
                onTimeUpdate={onTimeUpdateHandle}
                preload="auto"
                controls={controls}
                data-target-id="custom-audio-hidden-audio-element"
                src={media.url}
            />
            <AudioPlayer
                thumbnailUrl={thumbnail?.url}
                onPlayPause={playPauseHandle}
                playing={!audioRef.current?.paused}
                onTimeSliderChangeCommitted={updateCurrentTime}
                onTimeSliderChange={setTimeSliderValue}
                timeSliderValue={timeSliderValue}
                onVolumeChange={volumeSliderHandle}
                volume={volume}
                onMute={() => setMuted(!muted)}
                isMuted={muted}
                currentTime={currentTime}
                totalDuration={media.duration}
            />
        </>
    );
};

export default CustomAudio;
